/**
 * COMMERCE-LAUNCH-168D1 — isolated form field for Android Telegram / SwiftKey IME.
 * Local draft so keystrokes do not re-render the parent checkout tree or fight composition.
 * Authoritative value commits on blur; callers flush before submit.
 */
import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from "react";

export type IsolatedFormFieldProps = {
  id: string;
  label: ReactNode;
  value: string;
  onCommit: (next: string) => void;
  /** Optional normalize on commit/blur only — never during active composition. */
  commitFilter?: (raw: string) => string;
  onBlurExtra?: () => void;
  error?: ReactNode | null;
  hint?: ReactNode | null;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  maxLength?: number;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  requiredMark?: boolean;
  testId?: string;
  /** Optional forensic stamp (168D2). */
  imeTestId?: string;
  /** Register flush so parent can commit drafts before placeOrder without waiting for blur. */
  registerFlush?: (id: string, flush: () => string) => void;
};

function IsolatedFormFieldInner({
  id,
  label,
  value,
  onCommit,
  commitFilter,
  onBlurExtra,
  error,
  hint,
  autoComplete,
  inputMode,
  maxLength,
  placeholder,
  multiline,
  rows = 2,
  testId,
  imeTestId,
  registerFlush,
}: IsolatedFormFieldProps) {
  const [draft, setDraft] = useState(value);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const composingRef = useRef(false);
  const commitTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!composingRef.current) setDraft(value);
  }, [value]);

  useEffect(
    () => () => {
      if (commitTimerRef.current) window.clearTimeout(commitTimerRef.current);
    },
    [],
  );

  const flush = useCallback(() => {
    const raw = draftRef.current;
    const next = commitFilter ? commitFilter(raw) : raw;
    draftRef.current = next;
    setDraft(next);
    if (next !== value) onCommit(next);
    return next;
  }, [commitFilter, onCommit, value]);

  useEffect(() => {
    registerFlush?.(id, flush);
    return () => registerFlush?.(id, () => draftRef.current);
  }, [flush, id, registerFlush]);

  const common = {
    id,
    value: draft,
    autoComplete,
    autoCorrect: "off" as const,
    spellCheck: false as const,
    maxLength,
    placeholder,
    "data-testid": testId,
    ...(imeTestId ? { "data-ime-test": imeTestId } : {}),
    onCompositionStart: () => {
      composingRef.current = true;
    },
    onCompositionEnd: (e: React.CompositionEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      composingRef.current = false;
      setDraft(e.currentTarget.value);
    },
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = e.target.value;
      setDraft(next);
      // COMMERCE-LAUNCH-168D6 — deferred parent commit so checkout CTA readiness updates
      // without per-keystroke remount/IME fight. Skip while composing.
      if (commitTimerRef.current) window.clearTimeout(commitTimerRef.current);
      commitTimerRef.current = window.setTimeout(() => {
        if (composingRef.current) return;
        const filtered = commitFilter ? commitFilter(draftRef.current) : draftRef.current;
        if (filtered !== value) onCommit(filtered);
      }, 180);
    },
    onBlur: () => {
      if (commitTimerRef.current) {
        window.clearTimeout(commitTimerRef.current);
        commitTimerRef.current = null;
      }
      flush();
      onBlurExtra?.();
    },
  };

  return (
    <div className="sv2-field" data-isolated-field="1">
      <label htmlFor={id}>{label}</label>
      {multiline ? (
        <textarea {...common} rows={rows} />
      ) : (
        <input {...common} inputMode={inputMode} />
      )}
      {error ? <div className="sv2-field-err">{error}</div> : null}
      {!error && hint ? <div className="sv2-field-err">{hint}</div> : null}
    </div>
  );
}

export const IsolatedFormField = memo(IsolatedFormFieldInner);
